import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { LuArrowUpRight, LuBookmarkCheck, LuSearch } from 'react-icons/lu';
import { decodeHtmlEntities } from '../../utils/formatStructuredAnswer';
import NotebookAvatar from '../../pages/learn/NotebookAvatar';

function sourceLabel(source) {
  if (source === 'MODULE') return 'Module';
  return 'Catalog';
}

function sourceBadgeClass(source) {
  const key = (source || 'MANUAL').toLowerCase();
  if (key === 'module') return 'catalog-showcase-card-badge catalog-showcase-card-badge--module';
  if (key === 'public') return 'catalog-showcase-card-badge catalog-showcase-card-badge--community';
  return 'catalog-showcase-card-badge';
}

export default function LearnSubscribedNotebooks({
  readListEntries = [],
  publicNotebooks = [],
  busy,
  onUnsubscribeCatalog,
  onUnsubscribePublic,
  layout = 'stacked',
}) {
  const isColumn = layout === 'column';
  const [search, setSearch] = useState('');
  const catalogCount = readListEntries.length;
  const publicCount = publicNotebooks.length;
  const total = catalogCount + publicCount;

  const rows = useMemo(() => {
    const catalogRows = readListEntries.map((entry) => ({
      key: `catalog-${entry.topic}`,
      kind: 'catalog',
      topic: entry.topic,
      title: entry.topic,
      badge: sourceLabel(entry.source),
      badgeClass: sourceBadgeClass(entry.source),
      libraryTo: `/library?notebook=${encodeURIComponent(entry.topic)}`,
      openTo: null,
      notebook: null,
    }));
    const publicRows = publicNotebooks.map((nb) => ({
      key: `public-${nb.id}`,
      kind: 'public',
      topic: null,
      title: decodeHtmlEntities(nb.title || 'Untitled'),
      badge: 'Community',
      badgeClass: sourceBadgeClass('public'),
      libraryTo: null,
      openTo: `/hub/notebooks/${nb.id}`,
      notebook: nb,
    }));
    return [...catalogRows, ...publicRows];
  }, [readListEntries, publicNotebooks]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => (row.title || '').toLowerCase().includes(q));
  }, [rows, search]);

  const showEmptyFilter = total > 0 && filteredRows.length === 0;

  return (
    <section
      id="learn-subscriptions"
      className={`subscriptions-showcase${isColumn ? ' subscriptions-showcase--column' : ''}`}
      aria-labelledby="learn-subscribed-title"
    >
      <div className="catalog-showcase subscriptions-showcase-inner">
      <div className="catalog-showcase-hero catalog-showcase-hero--split">
        <div className="catalog-showcase-hero-top">
          <div className="catalog-showcase-hero-main">
            <span className="catalog-showcase-icon" aria-hidden>
              <LuBookmarkCheck />
            </span>
            <div className="catalog-showcase-copy">
              <p className="catalog-showcase-eyebrow">Your subscriptions</p>
              <h3 id="learn-subscribed-title" className="catalog-showcase-title">
                Currently subscribed
              </h3>
            </div>
          </div>
          <dl
            className="learn-page-stats learn-page-stats--hero catalog-showcase-metrics catalog-showcase-metrics--right"
            aria-live="polite"
          >
            <div>
              <dt>Subscribed</dt>
              <dd>{total}</dd>
            </div>
            <div>
              <dt>Catalog</dt>
              <dd>{catalogCount}</dd>
            </div>
            <div>
              <dt>Community</dt>
              <dd>{publicCount}</dd>
            </div>
          </dl>
        </div>
        <div className="catalog-showcase-lead-row">
          <p className="catalog-showcase-lead">
            Q&amp;A topics you subscribed to from the catalog or community — they stay in your Library read list until
            you unsubscribe.
          </p>
          <ol className="catalog-showcase-flow catalog-showcase-flow--inline" aria-label="How subscriptions work">
            <li><span>Read</span> in <Link to="/library">Library</Link></li>
            <li><span>Practice</span> saved cards</li>
            <li><span>Unsubscribe</span> anytime below</li>
          </ol>
        </div>
      </div>

      <div className="catalog-showcase-toolbar">
        <label className="catalog-showcase-search">
          <LuSearch className="catalog-showcase-search-icon" aria-hidden />
          <input
            type="search"
            className="library-read-search"
            placeholder="Search your subscriptions…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search subscribed Q&A"
          />
        </label>
      </div>

      <ul className="library-read-catalog-list hub-library-catalog-list catalog-showcase-grid subscriptions-showcase-grid">
        {filteredRows.map((row) => (
          <li key={row.key} className="library-read-catalog-row">
            <div className="library-read-catalog-main">
              <div className="library-read-catalog-avatar" aria-hidden>
                <NotebookAvatar size="sm" />
              </div>
              <div className="library-read-catalog-copy">
                <span className={row.badgeClass}>{row.badge}</span>
                <span className="library-read-catalog-title library-read-catalog-title--row">{row.title}</span>
              </div>
            </div>
            <div className="library-read-catalog-actions">
              {row.libraryTo ? (
                <Link to={row.libraryTo} className="library-read-preview-btn">
                  Library
                  <LuArrowUpRight aria-hidden />
                </Link>
              ) : (
                <Link to={row.openTo} className="library-read-preview-btn">
                  Open
                  <LuArrowUpRight aria-hidden />
                </Link>
              )}
              <button
                type="button"
                className="library-read-subscribe-btn library-read-unsub-btn"
                disabled={busy}
                onClick={() => (
                  row.kind === 'catalog'
                    ? onUnsubscribeCatalog(row.topic)
                    : onUnsubscribePublic(row.notebook)
                )}
              >
                Unsubscribe
              </button>
            </div>
          </li>
        ))}
        {total === 0 && (
          <li className="library-read-catalog-empty">
            <LuBookmarkCheck className="library-read-catalog-empty-icon" aria-hidden />
            <p className="library-read-catalog-empty-title">No subscriptions yet</p>
            <p className="library-read-catalog-empty-text">
              Subscribe from the public Q&amp;A catalog above, or add modules in Settings → Learning.
            </p>
          </li>
        )}
        {showEmptyFilter && (
          <li className="library-read-catalog-empty">
            <p className="library-read-catalog-empty-title">No subscriptions match</p>
            <p className="library-read-catalog-empty-text">Try a different search term.</p>
            <button
              type="button"
              className="library-read-catalog-empty-action"
              onClick={() => setSearch('')}
            >
              Clear search
            </button>
          </li>
        )}
      </ul>
      </div>
    </section>
  );
}
